import React from 'react';

interface IconProps {
  size?: number;
  className?: string;
}

// =====================================================
// HOME ICON - House with glowing window
// =====================================================
export function HomeIconAnimated({ size = 24, className = '' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <style>
        {`
          @keyframes windowGlow {
            0%, 100% { opacity: 0.6; }
            50% { opacity: 1; }
          }
          @keyframes chimneySmokeHome {
            0% { transform: translateY(0); opacity: 0.6; }
            100% { transform: translateY(-3px); opacity: 0; }
          }
          .window-glow {
            animation: windowGlow 2s ease-in-out infinite;
          }
          .smoke-home {
            animation: chimneySmokeHome 2s ease-out infinite;
          }
        `}
      </style>
      
      {/* Roof */}
      <path
        d="M3 10L12 3L21 10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      
      {/* House body */}
      <path
        d="M5 10V19C5 19.5523 5.44772 20 6 20H18C18.5523 20 19 19.5523 19 19V10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      
      {/* Door */}
      <rect
        x="10"
        y="14"
        width="4"
        height="6"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      
      {/* Window with glow */}
      <rect
        className="window-glow"
        x="14"
        y="12"
        width="3"
        height="3"
        fill="#FBBF24"
        rx="0.5"
      />
      
      {/* Chimney smoke */}
      <circle className="smoke-home" cx="17" cy="5" r="1" fill="currentColor" opacity="0.4" style={{ animationDelay: '0s' }} />
      <circle className="smoke-home" cx="17.5" cy="4" r="0.7" fill="currentColor" opacity="0.3" style={{ animationDelay: '0.5s' }} />
    </svg>
  );
}

// =====================================================
// COLLECTION ICON - Stacked cards with sparkle
// =====================================================
export function CollectionIconAnimated({ size = 24, className = '' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <style>
        {`
          @keyframes cardFloat {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-1px); }
          }
          @keyframes sparkleCollection {
            0%, 100% { opacity: 0; transform: scale(0); }
            50% { opacity: 1; transform: scale(1); }
          }
          .card-float-1 {
            animation: cardFloat 2s ease-in-out infinite;
          }
          .card-float-2 {
            animation: cardFloat 2s ease-in-out infinite;
            animation-delay: 0.3s;
          }
          .card-float-3 {
            animation: cardFloat 2s ease-in-out infinite;
            animation-delay: 0.6s;
          }
          .sparkle-collection {
            animation: sparkleCollection 1.5s ease-in-out infinite;
          }
        `}
      </style>
      
      {/* Back card */}
      <rect
        className="card-float-1"
        x="3"
        y="5"
        width="12"
        height="16"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        opacity="0.4"
      />
      
      {/* Middle card */}
      <rect
        className="card-float-2"
        x="6"
        y="4"
        width="12"
        height="16"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        opacity="0.7"
      />
      
      {/* Front card */}
      <rect
        className="card-float-3"
        x="9"
        y="3"
        width="12"
        height="16"
        rx="2"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
      />
      
      {/* Card design on front */}
      <circle
        className="card-float-3"
        cx="15"
        cy="9"
        r="2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      
      {/* Sparkles */}
      <g className="sparkle-collection">
        <path
          d="M20 6L20.5 7L21.5 7.5L20.5 8L20 9L19.5 8L18.5 7.5L19.5 7L20 6Z"
          fill="#FBBF24"
        />
      </g>
      <g className="sparkle-collection" style={{ animationDelay: '0.5s' }}>
        <path
          d="M4 14L4.3 14.7L5 15L4.3 15.3L4 16L3.7 15.3L3 15L3.7 14.7L4 14Z"
          fill="#FBBF24"
        />
      </g>
    </svg>
  );
}

// =====================================================
// COMMUNITY ICON - Connected hearts
// =====================================================
export function CommunityIconAnimated({ size = 24, className = '' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <style>
        {`
          @keyframes heartbeat {
            0%, 100% { transform: scale(1); }
            15% { transform: scale(1.15); }
            30% { transform: scale(1); }
            45% { transform: scale(1.1); }
            60% { transform: scale(1); }
          }
          @keyframes pulseRing {
            0% { transform: scale(1); opacity: 0.6; }
            100% { transform: scale(1.5); opacity: 0; }
          }
          @keyframes floatHeart {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-2px); }
          }
          .main-heart {
            transform-origin: 12px 13px;
            animation: heartbeat 1.5s ease-in-out infinite;
          }
          .pulse-ring {
            transform-origin: 12px 13px;
            animation: pulseRing 2s ease-out infinite;
          }
          .mini-heart-1 {
            animation: floatHeart 2s ease-in-out infinite;
          }
          .mini-heart-2 {
            animation: floatHeart 2s ease-in-out infinite;
            animation-delay: 0.5s;
          }
        `}
      </style>
      
      {/* Pulse ring */}
      <path
        className="pulse-ring"
        d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
        fill="none"
        stroke="#EC4899"
        strokeWidth="1"
        opacity="0.3"
      />
      
      {/* Main heart */}
      <path
        className="main-heart"
        d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
        fill="url(#heartGradient)"
        stroke="#EC4899"
        strokeWidth="1.5"
      />
      
      {/* Mini floating hearts */}
      <path
        className="mini-heart-1"
        d="M5 5.5C5 4.67 5.67 4 6.5 4c.5 0 .94.23 1.22.6.28-.37.72-.6 1.22-.6C9.77 4 10.44 4.67 10.44 5.5c0 1.1-1.72 2.5-2.72 3.5-1-1-2.72-2.4-2.72-3.5z"
        fill="#F472B6"
        opacity="0.6"
      />
      <path
        className="mini-heart-2"
        d="M17 4.5C17 3.67 17.67 3 18.5 3c.4 0 .75.18.98.48.23-.3.58-.48.98-.48.83 0 1.5.67 1.5 1.5 0 .88-1.38 2-2.18 2.8-.8-.8-2.18-1.92-2.18-2.8z"
        fill="#F472B6"
        opacity="0.5"
      />
      
      <defs>
        <linearGradient id="heartGradient" x1="12" y1="3" x2="12" y2="21" gradientUnits="userSpaceOnUse">
          <stop stopColor="#F472B6" />
          <stop offset="1" stopColor="#EC4899" />
        </linearGradient>
      </defs>
    </svg>
  );
}

// =====================================================
// MENU ICON - Animated hamburger
// =====================================================
export function MenuIconAnimated({ size = 24, className = '' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <style>
        {`
          @keyframes menuLine1 {
            0%, 100% { transform: translateX(0); }
            50% { transform: translateX(2px); }
          }
          @keyframes menuLine2 {
            0%, 100% { transform: scaleX(1); }
            50% { transform: scaleX(0.8); }
          }
          @keyframes menuLine3 {
            0%, 100% { transform: translateX(0); }
            50% { transform: translateX(-2px); }
          }
          .menu-line-1 {
            animation: menuLine1 3s ease-in-out infinite;
          }
          .menu-line-2 {
            transform-origin: center;
            animation: menuLine2 3s ease-in-out infinite;
            animation-delay: 0.2s;
          }
          .menu-line-3 {
            animation: menuLine3 3s ease-in-out infinite;
            animation-delay: 0.4s;
          }
        `}
      </style>
      
      <line
        className="menu-line-1"
        x1="4"
        y1="7"
        x2="20"
        y2="7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        className="menu-line-2"
        x1="4"
        y1="12"
        x2="20"
        y2="12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        className="menu-line-3"
        x1="4"
        y1="17"
        x2="20"
        y2="17"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

// =====================================================
// CARD INFO ICON - Card with info symbol and shine
// =====================================================
export function CardInfoIconAnimated({ size = 24, className = '' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <style>
        {`
          @keyframes cardShine {
            0%, 100% { opacity: 0; transform: translateX(-10px) rotate(-45deg); }
            50% { opacity: 0.6; transform: translateX(20px) rotate(-45deg); }
          }
          @keyframes infoGlow {
            0%, 100% { opacity: 0.6; }
            50% { opacity: 1; }
          }
          @keyframes cardFloat {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-1px); }
          }
          .card-shine {
            animation: cardShine 3s ease-in-out infinite;
          }
          .info-glow {
            animation: infoGlow 2s ease-in-out infinite;
          }
          .card-body-float {
            animation: cardFloat 3s ease-in-out infinite;
          }
        `}
      </style>
      
      {/* Card body */}
      <g className="card-body-float">
        <rect
          x="4"
          y="2"
          width="16"
          height="20"
          rx="2"
          stroke="currentColor"
          strokeWidth="1.5"
          fill="none"
        />
        
        {/* Shine effect */}
        <rect
          className="card-shine"
          x="4"
          y="2"
          width="4"
          height="20"
          fill="url(#shineGradient)"
          clipPath="url(#cardClip)"
        />
        
        {/* Info "i" symbol */}
        <circle
          className="info-glow"
          cx="12"
          cy="9"
          r="1.5"
          fill="#60A5FA"
        />
        <rect
          className="info-glow"
          x="11"
          y="12"
          width="2"
          height="6"
          rx="1"
          fill="#60A5FA"
        />
      </g>
      
      <defs>
        <linearGradient id="shineGradient" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="white" stopOpacity="0" />
          <stop offset="50%" stopColor="white" stopOpacity="0.3" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>
        <clipPath id="cardClip">
          <rect x="4" y="2" width="16" height="20" rx="2" />
        </clipPath>
      </defs>
    </svg>
  );
}

// =====================================================
// PRICING ICON - Chart with rising line and pulse
// =====================================================
export function PricingIconAnimated({ size = 24, className = '' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <style>
        {`
          @keyframes chartDraw {
            0% { stroke-dashoffset: 30; }
            100% { stroke-dashoffset: 0; }
          }
          @keyframes dotPulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.3); opacity: 0.8; }
          }
          @keyframes arrowBounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-2px); }
          }
          .chart-line {
            stroke-dasharray: 30;
            animation: chartDraw 2s ease-out forwards;
          }
          .chart-dot {
            transform-origin: center;
            animation: dotPulse 1.5s ease-in-out infinite;
          }
          .chart-dot-2 {
            animation-delay: 0.3s;
          }
          .chart-dot-3 {
            animation-delay: 0.6s;
          }
          .price-arrow {
            animation: arrowBounce 1s ease-in-out infinite;
          }
        `}
      </style>
      
      {/* Chart axes */}
      <path
        d="M4 4v16h16"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.4"
      />
      
      {/* Rising chart line */}
      <path
        className="chart-line"
        d="M6 16l4-4 4 2 6-8"
        stroke="#22C55E"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      
      {/* Data points */}
      <circle className="chart-dot" cx="6" cy="16" r="2" fill="#22C55E" />
      <circle className="chart-dot chart-dot-2" cx="10" cy="12" r="2" fill="#22C55E" />
      <circle className="chart-dot chart-dot-3" cx="14" cy="14" r="2" fill="#22C55E" />
      <circle className="chart-dot" cx="20" cy="6" r="2" fill="#22C55E" />
      
      {/* Up arrow indicator */}
      <g className="price-arrow">
        <path
          d="M18 4l2-1v3"
          stroke="#22C55E"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

// =====================================================
// SIMILAR CARDS ICON - Stacked cards with sparkle
// =====================================================
export function SimilarCardsIconAnimated({ size = 24, className = '' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <style>
        {`
          @keyframes stackSlide1 {
            0%, 100% { transform: translate(0, 0); }
            50% { transform: translate(-1px, 1px); }
          }
          @keyframes stackSlide2 {
            0%, 100% { transform: translate(0, 0); }
            50% { transform: translate(1px, -1px); }
          }
          @keyframes linkPulse {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 0.8; }
          }
          @keyframes matchSparkle {
            0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
            50% { opacity: 1; transform: scale(1) rotate(180deg); }
          }
          .stack-card-1 {
            animation: stackSlide1 2.5s ease-in-out infinite;
          }
          .stack-card-2 {
            animation: stackSlide2 2.5s ease-in-out infinite;
            animation-delay: 0.2s;
          }
          .link-line {
            animation: linkPulse 2s ease-in-out infinite;
          }
          .match-sparkle {
            transform-origin: center;
            animation: matchSparkle 2s ease-in-out infinite;
          }
          .match-sparkle-2 {
            animation-delay: 0.5s;
          }
        `}
      </style>
      
      {/* Back card */}
      <rect
        className="stack-card-1"
        x="2"
        y="4"
        width="10"
        height="14"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        opacity="0.5"
      />
      
      {/* Front card */}
      <rect
        className="stack-card-2"
        x="6"
        y="6"
        width="10"
        height="14"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
      />
      
      {/* Connection lines */}
      <path
        className="link-line"
        d="M16 10h2M16 13h2M16 16h2"
        stroke="#A855F7"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      
      {/* Third card (match result) */}
      <rect
        x="18"
        y="8"
        width="4"
        height="10"
        rx="1"
        stroke="#A855F7"
        strokeWidth="1.5"
        fill="none"
      />
      
      {/* Match sparkles */}
      <g className="match-sparkle">
        <path
          d="M20 5l0.3 0.7 0.7 0.3-0.7 0.3-0.3 0.7-0.3-0.7-0.7-0.3 0.7-0.3z"
          fill="#FBBF24"
        />
      </g>
      <g className="match-sparkle match-sparkle-2">
        <path
          d="M4 18l0.2 0.5 0.5 0.2-0.5 0.2-0.2 0.5-0.2-0.5-0.5-0.2 0.5-0.2z"
          fill="#FBBF24"
        />
      </g>
    </svg>
  );
}

